import React, { useState } from 'react';
import { FaTimes, FaPlay, FaBug, FaFolder, FaFolderOpen, FaSave, FaFile, FaProjectDiagram, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { VscNewFile, VscNewFolder } from 'react-icons/vsc';
import { DiJavascript1, DiPhp, DiCss3, DiHtml5, DiReact } from 'react-icons/di';
import Editor from '@monaco-editor/react';
import FlowchartEditor from './FlowchartEditor';

const DevelopmentModal = ({ isOpen, onClose }) => {
  // Ana state'ler
  const [isDebugging, setIsDebugging] = useState(false);
  const [consoleOutput, setConsoleOutput] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('javascript');
  const [editorValue, setEditorValue] = useState('');
  const [files, setFiles] = useState({});
  const [showNewFileModal, setShowNewFileModal] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [currentPath, setCurrentPath] = useState('');
  const [debugInfo, setDebugInfo] = useState({
    variables: [],
    breakpoints: [],
    callStack: []
  });
  const [showFlowchart, setShowFlowchart] = useState(false);
  const [showConsole, setShowConsole] = useState(true);

  // Yeni dosya/klasör oluşturma modalları
  const NewFileModal = () => {
    const [fileName, setFileName] = useState('');
    const [fileType, setFileType] = useState('javascript');

    const handleCreate = () => {
      if (!fileName) return;
      
      const extension = fileType === 'javascript' ? '.js' : '.php';
      const newPath = currentPath ? `${currentPath}/${fileName}${extension}` : `${fileName}${extension}`;
      
      // Dosya sistemini güncelle
      setFiles(prev => {
        const newFiles = { ...prev };
        
        // Eğer bir klasör yolu varsa, o klasöre dosyayı ekle
        if (currentPath) {
          let current = newFiles;
          const paths = currentPath.split('/');
          
          // Klasör yolunu takip et
          for (let i = 0; i < paths.length; i++) {
            if (i === paths.length - 1) {
              // Son klasöre dosyayı ekle
              current[paths[i]] = {
                ...current[paths[i]],
                [fileName + extension]: fileType === 'javascript' ? 
                  '// New JavaScript File\n' :
                  '<?php\n// New PHP File\n'
              };
            } else {
              current = current[paths[i]];
            }
          }
        } else {
          // Root seviyesinde dosya oluştur
          newFiles[fileName + extension] = fileType === 'javascript' ? 
            '// New JavaScript File\n' :
            '<?php\n// New PHP File\n';
        }
        
        return newFiles;
      });
      
      setShowNewFileModal(false);
      setSelectedFile(newPath);
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
        <div className="bg-[#2d2d2d] p-4 rounded-lg w-96">
          <h3 className="text-white mb-4">
            Create New File {currentPath && <span className="text-gray-400">in /{currentPath}</span>}
          </h3>
          <input
            type="text"
            placeholder="File name"
            className="w-full bg-[#1e1e1e] text-white p-2 rounded mb-4"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
          />
          <select
            className="w-full bg-[#1e1e1e] text-white p-2 rounded mb-4"
            value={fileType}
            onChange={(e) => setFileType(e.target.value)}
          >
            <option value="javascript">JavaScript</option>
            <option value="php">PHP</option>
          </select>
          <div className="flex justify-end space-x-2">
            <button
              className="px-4 py-2 bg-gray-600 text-white rounded"
              onClick={() => setShowNewFileModal(false)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded"
              onClick={handleCreate}
            >
              Create
            </button>
          </div>
        </div>
      </div>
    );
  };

  const NewFolderModal = () => {
    const [folderName, setFolderName] = useState('');

    const handleCreate = () => {
      if (!folderName) return;
      
      const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
      
      setFiles(prev => ({
        ...prev,
        [newPath]: {}
      }));
      
      setShowNewFolderModal(false);
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
        <div className="bg-[#2d2d2d] p-4 rounded-lg w-96">
          <h3 className="text-white mb-4">Create New Folder</h3>
          <input
            type="text"
            placeholder="Folder name"
            className="w-full bg-[#1e1e1e] text-white p-2 rounded mb-4"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
          />
          <div className="flex justify-end space-x-2">
            <button
              className="px-4 py-2 bg-gray-600 text-white rounded"
              onClick={() => setShowNewFolderModal(false)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded"
              onClick={handleCreate}
            >
              Create
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Dosya içeriğini al
  const getFileContent = (filename) => {
    return files[filename] || '';
  };

  // Dosya seçimi
  const handleFileSelect = (filename) => {
    setSelectedFile(filename);
    const fileExt = filename.split('.').pop();
    setSelectedLanguage(fileExt === 'php' ? 'php' : 'javascript');
    setEditorValue(getFileContent(filename));
  };

  // Dosya kaydetme
  const handleSave = () => {
    if (selectedFile) {
      setFiles(prev => ({
        ...prev,
        [selectedFile]: editorValue
      }));
      addToConsole('success', 'File saved successfully');
    }
  };

  // Console mesajları
  const addToConsole = (type, message) => {
    setConsoleOutput(prev => [...prev, {
      id: Date.now(),
      type,
      message: typeof message === 'object' ? JSON.stringify(message, null, 2) : message,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  // JavaScript kodu çalıştırma
  const runJavaScript = () => {
    if (process.env.NODE_ENV === 'production') {
      addToConsole('error', 'JavaScript çalıştırma üretim ortamında devre dışı.');
      return;
    }
    try {
      const originalConsole = window.console.log;
      const logs = [];
      window.console.log = (...args) => {
        logs.push(args.join(' '));
        originalConsole.apply(console, args);
      };

      // Kodu çalıştır
      const result = new Function(editorValue)();
      
      // Yakalanan logları konsola ekle
      logs.forEach(log => addToConsole('info', log));
      
      // Eğer fonksiyon bir değer döndürürse onu da göster
      if (result !== undefined) {
        addToConsole('success', result);
      }

      window.console.log = originalConsole;
    } catch (error) {
      addToConsole('error', error.message);
    }
  };

  // PHP kodu çalıştırma
  const runPHP = async () => {
    if (process.env.NODE_ENV === 'production') {
      addToConsole('error', 'PHP çalıştırma üretim ortamında devre dışı.');
      return;
    }
    try {
      const response = await fetch('http://localhost:5000/run-php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: editorValue }),
      });
      
      const data = await response.json();
      
      if (data.error) {
        addToConsole('error', data.error);
      } else {
        addToConsole('success', data.output);
      }
    } catch (error) {
      addToConsole('error', 'PHP Server Error: ' + error.message);
    }
  };

  // Kod çalıştırma
  const runCode = async () => {
    addToConsole('info', `Running ${selectedLanguage.toUpperCase()} code...`);
    
    if (selectedLanguage === 'javascript') {
      runJavaScript();
    } else if (selectedLanguage === 'php') {
      await runPHP();
    }
  };

  // Debug işlemleri
  const debugActions = {
    start: () => {
      setIsDebugging(true);
      if (selectedLanguage === 'javascript') {
        try {
          // Basit değişken analizi
          const variables = [];
          const code = editorValue;
          
          // Değişken tanımlamalarını bul
          const varRegex = /(var|let|const)\s+(\w+)\s*=\s*([^;]+)/g;
          let match;
          
          while ((match = varRegex.exec(code)) !== null) {
            variables.push({
              name: match[2] ? String(match[2]) : '',
              value: match[3] ? String(match[3].trim()) : '',
              type: match[1] ? String(match[1]) : ''
            });
          }

          setDebugInfo(prev => ({
            ...prev,
            variables
          }));

          addToConsole('info', 'Debug session started');
        } catch (error) {
          addToConsole('error', 'Debug error: ' + error.message);
        }
      } else {
        addToConsole('info', 'PHP debugging requires Xdebug configuration');
      }
    },
    stop: () => {
      setIsDebugging(false);
      setDebugInfo({
        variables: [],
        breakpoints: [],
        callStack: []
      });
      addToConsole('info', 'Debug session ended');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1e1e1e] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#2d2d2d] border-b border-gray-700">
        <div className="flex items-center space-x-4">
          <h2 className="text-white font-medium">Development IDE</h2>
          
          {/* Language Selector */}
          <select
            className="bg-[#383838] text-white px-2 py-1 rounded"
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
          >
            <option value="javascript">JavaScript</option>
            <option value="php">PHP</option>
          </select>

          {/* Action Buttons */}
          <button
            onClick={handleSave}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md 
                     flex items-center space-x-2 transition-colors"
          >
            <FaSave className="text-sm" />
            <span>Save</span>
          </button>
          
          <button
            onClick={runCode}
            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md 
                     flex items-center space-x-2 transition-colors"
          >
            <FaPlay className="text-sm" />
            <span>Run</span>
          </button>
          
          <button
            onClick={isDebugging ? debugActions.stop : debugActions.start}
            className={`px-3 py-1.5 ${
              isDebugging ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
            } text-white rounded-md flex items-center space-x-2 transition-colors`}
          >
            <FaBug className="text-sm" />
            <span>{isDebugging ? 'Stop Debug' : 'Debug'}</span>
          </button>

          {/* DiagRun Button */}
          <button
            onClick={() => setShowFlowchart(!showFlowchart)}
            className={`px-3 py-1.5 ${
              showFlowchart ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-600 hover:bg-gray-700'
            } text-white rounded-md flex items-center space-x-2 transition-colors`}
          >
            <FaProjectDiagram className="text-sm" />
            <span>DiagRun</span>
          </button>
        </div>

        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white 
                   transition-all duration-200"
        >
          <FaTimes />
        </button>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* File Explorer */}
        <div className="w-64 bg-[#252526] overflow-y-auto">
          <div className="p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-300 text-sm font-medium">Files</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowNewFileModal(true)}
                  className="p-1 hover:bg-[#2a2d2e] rounded"
                  title="New File"
                >
                  <FaPlay className="text-gray-400 hover:text-white" />
                </button>
                <button
                  onClick={() => setShowNewFolderModal(true)}
                  className="p-1 hover:bg-[#2a2d2e] rounded"
                  title="New Folder"
                >
                  <FaFolder className="text-gray-400 hover:text-white" />
                </button>
              </div>
            </div>
            
            {/* File Tree */}
            <FileTree
              files={files}
              onSelect={handleFileSelect}
              selectedFile={selectedFile}
              setCurrentPath={setCurrentPath}
              setShowNewFileModal={setShowNewFileModal}
              setShowNewFolderModal={setShowNewFolderModal}
            />
          </div>
        </div>

        {/* Editor and Console */}
        <div className="flex-1 flex flex-col">
          <Editor
            height={showConsole ? "70%" : "100%"}
            language={selectedLanguage}
            value={editorValue}
            theme="vs-dark"
            onChange={(value) => setEditorValue(value)}
            options={{
              fontSize: 14,
              minimap: { enabled: true },
              automaticLayout: true,
              lineNumbers: 'on',
              roundedSelection: true,
              cursorStyle: 'line',
              cursorWidth: 2,
              cursorBlinking: 'smooth',
              smoothScrolling: true,
              folding: true,
              contextmenu: true,
              suggestOnTriggerCharacters: true,
              formatOnPaste: true,
              formatOnType: true,
            }}
          />

          {/* Console - Toggle özelliği eklenmiş hali */}
          <div className={`bg-[#1e1e1e] border-t border-gray-700 transition-all duration-300 ${
            showConsole ? 'h-[30%]' : 'h-[30px]'
          }`}>
            <div 
              className="flex items-center justify-between px-4 py-1 bg-[#2d2d2d] border-b border-gray-700 cursor-pointer"
              onClick={() => setShowConsole(!showConsole)}
            >
              <span className="text-gray-300 text-sm">Console</span>
              <button className="text-gray-400 hover:text-white">
                {showConsole ? <FaChevronDown /> : <FaChevronUp />}
              </button>
            </div>
            {showConsole && (
              <div className="h-[calc(100%-30px)] overflow-y-auto p-2">
                {consoleOutput.map(log => (
                  <div
                    key={log.id}
                    className={`mb-1 text-sm font-mono ${
                      log.type === 'error'
                        ? 'text-red-400'
                        : log.type === 'success'
                        ? 'text-green-400'
                        : 'text-gray-300'
                    }`}
                  >
                    <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Debug Panel */}
        {isDebugging && (
          <div className="w-64 bg-[#252526] border-l border-gray-700 p-3">
            <h3 className="text-gray-300 text-sm font-medium mb-3">Debug Info</h3>
            
            {/* Variables */}
            <div className="mb-4">
              <h4 className="text-gray-400 text-xs uppercase mb-2">Variables</h4>
              {debugInfo.variables.map((variable, index) => (
                <div key={index} className="text-sm text-gray-300 mb-1">
                  <span className="text-blue-400">{variable.type}</span>{' '}
                  <span className="text-yellow-400">{variable.name}</span> ={' '}
                  <span className="text-green-400">{variable.value}</span>
                </div>
              ))}
            </div>

            {/* Breakpoints */}
            <div className="mb-4">
              <h4 className="text-gray-400 text-xs uppercase mb-2">Breakpoints</h4>
              <div className="text-sm text-gray-300">No breakpoints set</div>
            </div>

            {/* Call Stack */}
            <div className="mb-4">
              <h4 className="text-gray-400 text-xs uppercase mb-2">Call Stack</h4>
              <div className="text-sm text-gray-300">Main (anonymous)</div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showNewFileModal && <NewFileModal />}
      {showNewFolderModal && <NewFolderModal />}

      {/* Flowchart Editor - Sağdan açılır panel */}
      {showFlowchart && (
        <div 
          className="w-1/2 border-l border-gray-700 absolute right-0 top-0 bottom-0 bg-[#1e1e1e] 
                   transition-transform duration-300 transform"
          style={{
            transform: showFlowchart ? 'translateX(0)' : 'translateX(100%)',
            height: '100%'
          }}
        >
          <FlowchartEditor />
        </div>
      )}
    </div>
  );
};

// FileTree bileşeni
const FileTree = ({ 
  files, 
  onSelect, 
  selectedFile, 
  setCurrentPath,
  setShowNewFileModal,
  setShowNewFolderModal 
}) => {
  // Klasörlerin açık/kapalı durumunu tutmak için state
  const [expandedFolders, setExpandedFolders] = useState({});

  // Dosya ikonu seçici
  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    switch (ext) {
      case 'js':
        return <DiJavascript1 className="text-yellow-400" />;
      case 'php':
        return <DiPhp className="text-purple-400" />;
      case 'jsx':
        return <DiReact className="text-blue-400" />;
      case 'css':
        return <DiCss3 className="text-blue-500" />;
      case 'html':
        return <DiHtml5 className="text-orange-500" />;
      default:
        return <FaFile className="text-gray-400" />;
    }
  };

  // Klasör durumunu değiştirme
  const toggleFolder = (path) => {
    setExpandedFolders(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const renderTree = (items, path = '') => {
    return Object.entries(items).map(([key, value]) => {
      const fullPath = path ? `${path}/${key}` : key;
      
      if (typeof value === 'string') {
        // Dosya
        return (
          <div
            key={fullPath}
            onClick={() => onSelect(fullPath)}
            className={`px-2 py-1.5 rounded cursor-pointer text-sm flex items-center group
              ${selectedFile === fullPath
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:bg-[#2a2d2e]'
              }`}
          >
            <span className="mr-2">
              {getFileIcon(key)}
            </span>
            <span className="flex-1">{key}</span>
          </div>
        );
      } else {
        // Klasör
        const isExpanded = expandedFolders[fullPath];
        
        return (
          <div key={fullPath}>
            <div
              className="group px-2 py-1.5 text-sm text-gray-300 font-medium 
                       cursor-pointer hover:bg-[#2a2d2e] flex items-center justify-between"
              onClick={() => toggleFolder(fullPath)}
            >
              <span className="flex items-center">
                {isExpanded ? 
                  <FaFolderOpen className="mr-2 text-yellow-500" /> : 
                  <FaFolder className="mr-2 text-yellow-500" />
                }
                {key}
              </span>
              <div className="hidden group-hover:flex space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentPath(fullPath);
                    setShowNewFileModal(true);
                  }}
                  className="p-1 hover:bg-[#3a3d3e] rounded"
                  title="New File"
                >
                  <VscNewFile className="text-gray-400 hover:text-white" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentPath(fullPath);
                    setShowNewFolderModal(true);
                  }}
                  className="p-1 hover:bg-[#3a3d3e] rounded"
                  title="New Folder"
                >
                  <VscNewFolder className="text-gray-400 hover:text-white" />
                </button>
              </div>
            </div>
            {isExpanded && (
              <div className="pl-4 border-l border-gray-700 ml-2">
                {renderTree(value, fullPath)}
              </div>
            )}
          </div>
        );
      }
    });
  };

  return (
    <div>
      <div className="mb-4">{renderTree(files)}</div>
      
      {/* Root seviyesi için butonlar */}
      <div className="px-2 space-y-1">
        <button
          onClick={() => {
            setCurrentPath('');
            setShowNewFileModal(true);
          }}
          className="w-full p-1.5 hover:bg-[#2a2d2e] rounded flex items-center 
                   text-sm text-gray-400 hover:text-white group"
        >
          <VscNewFile className="mr-2" />
          <span className="flex-1 text-left">New File</span>
        </button>
        <button
          onClick={() => {
            setCurrentPath('');
            setShowNewFolderModal(true);
          }}
          className="w-full p-1.5 hover:bg-[#2a2d2e] rounded flex items-center 
                   text-sm text-gray-400 hover:text-white group"
        >
          <VscNewFolder className="mr-2" />
          <span className="flex-1 text-left">New Folder</span>
        </button>
      </div>
    </div>
  );
};

export default DevelopmentModal; 
